'use client';

import { useTranslation } from '@/hooks/useTranslation';
import BookDemoSection from '@/components/BookDemoSection';
import { FaqAccordion, PageSection, SectionTitle } from '@/components/ui';
import { MetricsBand } from '@/components/primitives/MetricsBand';
import EnergyHero from './EnergyHero';
import IndustryBattleground from '@/components/industry/IndustryBattleground';
import IndustryChallenges from '@/components/industry/IndustryChallenges';
import PlatformCapabilities from '@/components/industry/PlatformCapabilities';
import IndustryResults from '@/components/industry/IndustryResults';
import { getIndustryContent } from '@/lib/industryContent';
import '@/components/industry/industry-page.css';

/* Problem-framing stats for the MetricsBand — sourced from EnergyHero FEATURED + STATS */
const METRICS = [
  { value: '60%', label: 'of utility consumers complain about outage communication first — before the outage itself (J.D. Power Electric Utility Study 2025)' },
  { value: '70%', label: "of energy consumers say they won't invest additional time or money in clean energy without stronger trust in their provider (EY Global Consumer Energy Survey 2025)" },
  { value: '40×', label: 'faster brand trust erosion for utilities that go silent during outages vs those with proactive social media communication (McKinsey Energy CX 2024)' },
];

export default function EnergyBody() {
  const { t } = useTranslation();
  const C = getIndustryContent('energy-utilities');

  const CHALLENGES = [
    {
      title: t("industries_energy_utilities.the_outage_communication_gap"),
      desc: t("industries_energy_utilities.a_grid_failure_triggers_hundreds_of"),
    },
    {
      title: t("industries_energy_utilities.the_billing_dispute_flood"),
      desc: t("industries_energy_utilities.utility_billing_complaint_managemen"),
    },
    {
      title: t("industries_energy_utilities.the_renewable_project_opposition_ri"),
      desc: t("industries_energy_utilities.wind_farms_solar_parks_and_transmis"),
    },
    {
      title: t("industries_energy_utilities.the_smart_meter_trust_deficit"),
      desc: t("industries_energy_utilities.smart_meter_and_prepaid_meter_deplo"),
    },
  ];

  const FAQS = [
    {
      q: t("industries_energy_utilities.what_is_an_energy_utilities_consume"),
      a: t("industries_energy_utilities.one_workspace_for_power_outage_comp"),
    },
    {
      q: t("industries_energy_utilities.how_does_locobuzz_help_utilities_co"),
      a: t("industries_energy_utilities.social_channels_monitored_24_7_when"),
    },
    {
      q: t("industries_energy_utilities.can_locobuzz_monitor_community_sent"),
      a: t("industries_energy_utilities.yes_project_level_social_listening"),
    },
    {
      q: t("industries_energy_utilities.how_does_locobuzz_help_utilities_ma"),
      a: t("industries_energy_utilities.smart_meter_rollout_social_conversa"),
    },
    {
      q: t("industries_energy_utilities.can_locobuzz_monitor_esg_reputation"),
      a: t("industries_energy_utilities.yes_esg_commitments_and_green_energ"),
    },
  ];

  const BG_TITLE = t('industries_energy_utilities.why_is_consumer_experience_now_a_st');
  const BG_ACCENT = 'Energy & Utilities';
  const _bgCut = BG_TITLE.lastIndexOf(BG_ACCENT);
  const bgTitle = _bgCut > 0 ? BG_TITLE.slice(0, _bgCut).trim() : BG_TITLE;
  const bgAccent = _bgCut > 0 ? BG_TITLE.slice(_bgCut) : undefined;

  return (
    <div className="industry-page-wrap">
      <span className="sr-only">{t("industries_energy_utilities.locobuzz_is_an_ainative_consumer_ex")}</span>
      <span className="sr-only">{t("industries_energy_utilities.utility_consumers_now_compare_their")}</span>
      <span className="sr-only">{t("industries_energy_utilities.locobuzz_maps_six_purposebuilt_capa")}</span>
      <span className="sr-only">{t("industries_energy_utilities.energy_and_utilities_brands_using_l")}</span>

      <EnergyHero />

      <MetricsBand items={METRICS} columns={3} />

      <IndustryBattleground
        eyebrow={t("industries_energy_utilities.why_consumer_experience_is_now_a_st")}
        title={bgTitle}
        titleAccent={bgAccent}
        paragraph={t("industries_energy_utilities.the_gap_between_utility_consumer_sa")}
        imageSrc="/industries/why/energy-utilities.webp"
        imageAlt={t("industries_energy_utilities.energy_and_utilities_outage_sentime")}
        bg="tint"
      />

      <IndustryChallenges
        title={t("industries_energy_utilities.what_consumer_cx_challenges_do_powe")}
        items={CHALLENGES}
        align="center"
        bg="page"
      />

      <PlatformCapabilities {...C.capabilities} />

      <IndustryResults {...C.results} />

      <PageSection id="faqs" bg="tint">
        <SectionTitle
          title={
            <>
              {t('faq.everything_you_need_to')}{' '}
              <span className="serif-accent italic">{t('faq.know')}</span>
            </>
          }
          align="center"
        />
        <FaqAccordion items={FAQS} columns={2} name="energy-faq" />
      </PageSection>

      <BookDemoSection />
    </div>
  );
}
